import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuthStore } from './store';
import { authAPI } from './services/api';

// Critical components - loaded immediately
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import LandingPage from './pages/LandingPage';
import Layout from './components/Layout';
import { initializeStoreConfig } from './utils/appConfig';

// Lazy loaded components - loaded on demand
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Products = lazy(() => import('./pages/Products'));
const CashRegister = lazy(() => import('./pages/CashRegister'));
const Clients = lazy(() => import('./pages/Clients'));
const RepairsModule = lazy(() => import('./modules/RepairsModule'));
const Quotes = lazy(() => import('./pages/Quotes'));
const Reservations = lazy(() => import('./pages/Reservations'));
const SuppliersModule = lazy(() => import('./modules/SuppliersModule'));
const ShoppingModule = lazy(() => import('./modules/ShoppingModule'));
const OrderManagement = lazy(() => import('./pages/OrderManagement'));
const ReceptionManagement = lazy(() => import('./pages/ReceptionManagement'));
const PurchaseManagement = lazy(() => import('./pages/PurchaseManagement'));
const Appointments = lazy(() => import('./pages/Appointments'));
const Finance = lazy(() => import('./pages/Finance'));
const Settings = lazy(() => import('./pages/Settings'));
const UsersManagement = lazy(() => import('./pages/UsersManagement'));
const AccountsManagement = lazy(() => import('./pages/AccountsManagement'));

// Loading component for lazy routes
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
  </div>
);

// Protection des routes
function isPlatformOnlySession(user) {
  return Boolean((user?.is_staff || user?.is_superuser) && !user?.account_code && !user?.current_account);
}

function PrivateRoute({ children }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);
  const location = useLocation();
  if (isAuthenticated && isPlatformOnlySession(user) && location.pathname !== '/accounts') {
    return <Navigate to="/accounts" replace />;
  }
  return isAuthenticated ? children : <Navigate to="/login" />;
}

function PermissionRoute({ permission, children }) {
  const user = useAuthStore((state) => state.user);

  if (!user?.[permission]) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function PlatformAdminRoute({ children }) {
  const user = useAuthStore((state) => state.user);

  if (!(user?.is_staff || user?.is_superuser)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function App() {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AppContent />
    </Router>
  );
}

function AppContent() {
  const location = useLocation();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const updateUser = useAuthStore((state) => state.updateUser);
  const logout = useAuthStore((state) => state.logout);
  const [isAppReady, setIsAppReady] = useState(() => !isAuthenticated);
  const isPublicRoute = ['/', '/login', '/register', '/forgot-password'].includes(location.pathname)
    || location.pathname.startsWith('/reset-password/');

  useEffect(() => {
    if (!isAuthenticated) {
      setIsAppReady(true);
      return;
    }

    let isMounted = true;
    setIsAppReady(false);

    authAPI.getCurrentUser()
      .then(async (response) => {
        const nextUser = response.data;
        if (isMounted) {
          updateUser(nextUser);
        }
        if (!isPlatformOnlySession(nextUser)) {
          await initializeStoreConfig();
        }
      })
      .catch(() => {
        if (isMounted) {
          logout();
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsAppReady(true);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, logout, updateUser]);

  if (!isAppReady && !isPublicRoute) {
    return <LoadingFallback />;
  }

  return (
    <div className="App">
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:uid/:token" element={<ForgotPassword />} />
        <Route path="/dashboard" element={
          <PrivateRoute>
            <Layout>
              <Suspense fallback={<LoadingFallback />}>
                <Dashboard />
              </Suspense>
            </Layout>
          </PrivateRoute>
        } />
        <Route path="/products" element={
          <PrivateRoute>
            <Layout>
              <Suspense fallback={<LoadingFallback />}>
                <Products />
              </Suspense>
            </Layout>
          </PrivateRoute>
        } />
        <Route path="/cashier" element={
          <PrivateRoute>
            <Layout>
              <Suspense fallback={<LoadingFallback />}>
                <CashRegister />
              </Suspense>
            </Layout>
          </PrivateRoute>
        } />
        <Route path="/clients" element={
          <PrivateRoute>
            <Layout>
              <Suspense fallback={<LoadingFallback />}>
                <Clients />
              </Suspense>
            </Layout>
          </PrivateRoute>
        } />
        <Route path="/repairs" element={
          <PrivateRoute>
            <Layout>
              <Suspense fallback={<LoadingFallback />}>
                <RepairsModule />
              </Suspense>
            </Layout>
          </PrivateRoute>
        } />
        <Route path="/repairs/:id" element={
          <PrivateRoute>
            <Layout>
              <Suspense fallback={<LoadingFallback />}>
                <RepairsModule />
              </Suspense>
            </Layout>
          </PrivateRoute>
        } />
        <Route path="/quotes" element={
          <PrivateRoute>
            <Layout>
              <Suspense fallback={<LoadingFallback />}>
                <Quotes />
              </Suspense>
            </Layout>
          </PrivateRoute>
        } />
        <Route path="/reservations" element={
          <PrivateRoute>
            <Layout>
              <Suspense fallback={<LoadingFallback />}>
                <Reservations />
              </Suspense>
            </Layout>
          </PrivateRoute>
        } />
        <Route path="/suppliers" element={
          <PrivateRoute>
            <Layout>
              <Suspense fallback={<LoadingFallback />}>
                <SuppliersModule />
              </Suspense>
            </Layout>
          </PrivateRoute>
        } />
        <Route path="/shopping" element={
          <PrivateRoute>
            <Layout>
              <Suspense fallback={<LoadingFallback />}>
                <ShoppingModule />
              </Suspense>
            </Layout>
          </PrivateRoute>
        } />
        <Route path="/orders" element={
          <PrivateRoute>
            <Layout>
              <Suspense fallback={<LoadingFallback />}>
                <OrderManagement />
              </Suspense>
            </Layout>
          </PrivateRoute>
        } />
        <Route path="/receptions" element={
          <PrivateRoute>
            <Layout>
              <Suspense fallback={<LoadingFallback />}>
                <ReceptionManagement />
              </Suspense>
            </Layout>
          </PrivateRoute>
        } />
        <Route path="/purchases" element={
          <PrivateRoute>
            <Layout>
              <Suspense fallback={<LoadingFallback />}>
                <SuppliersModule />
              </Suspense>
            </Layout>
          </PrivateRoute>
        } />
        <Route path="/appointments" element={
          <PrivateRoute>
            <Layout>
              <Suspense fallback={<LoadingFallback />}>
                <Appointments />
              </Suspense>
            </Layout>
          </PrivateRoute>
        } />
        <Route path="/finance" element={
          <PrivateRoute>
            <PermissionRoute permission="can_view_analytics">
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <Finance />
                </Suspense>
              </Layout>
            </PermissionRoute>
          </PrivateRoute>
        } />
        <Route path="/settings" element={
          <PrivateRoute>
            <Layout>
              <Suspense fallback={<LoadingFallback />}>
                <Settings />
              </Suspense>
            </Layout>
          </PrivateRoute>
        } />
        <Route path="/general" element={
          <PrivateRoute>
            <Layout>
              <Suspense fallback={<LoadingFallback />}>
                <Settings />
              </Suspense>
            </Layout>
          </PrivateRoute>
        } />
        <Route path="/users" element={
          <PrivateRoute>
            <PermissionRoute permission="can_manage_users">
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <UsersManagement />
                </Suspense>
              </Layout>
            </PermissionRoute>
          </PrivateRoute>
        } />
        <Route path="/accounts" element={
          <PrivateRoute>
            <PlatformAdminRoute>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <AccountsManagement />
                </Suspense>
              </Layout>
            </PlatformAdminRoute>
          </PrivateRoute>
        } />
      </Routes>
      <Toaster
        position="top-center"
        gutter={10}
        containerStyle={{
          top: 18,
          left: 0,
          right: 0,
        }}
        toastOptions={{
          duration: 2200,
          style: {
            background: 'rgba(15, 23, 42, 0.88)',
            color: '#fff',
            borderRadius: '18px',
            padding: '12px 14px',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            boxShadow: '0 18px 50px -24px rgba(15, 23, 42, 0.55)',
            border: '1px solid rgba(255, 255, 255, 0.10)',
            fontSize: '14px',
            lineHeight: '1.35',
            maxWidth: 'min(92vw, 420px)',
          },
          success: {
            duration: 2000,
            iconTheme: {
              primary: '#34d399',
              secondary: '#0f172a',
            },
          },
          error: {
            duration: 2800,
            iconTheme: {
              primary: '#f87171',
              secondary: '#0f172a',
            },
          },
        }}
      />
    </div>
  );
}

export default App;
